import React from "react";
import { Box, Typography } from "@mui/material";
import logo from "../../../Logo/Logo.png";
import { API_BASE_URL } from "../../../utils/api";
import DOMPurify from 'dompurify';

const formatDate = (d) =>
  new Date(d || Date.now()).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const ViolationLetterTemplate = ({
  type,
  member = {},
  violation = {},
  date,
  content = "",
  formattedAddress,
  deliveryMethod,
  logoUrl,
}) => {
  const today = formatDate(date);

  // 🔹 Placeholders
  const tags = {
    date: today,
    name: `${member?.FirstName || ""} ${member?.LastName || ""}`,
    address: formattedAddress || member?.FullAddressHtml || "",
    email: member?.EmailAddress || "",
    property_address: member?.FullAddress || "",
    violation_type: violation?.type || "",
    violation_rule: violation?.rule || "",
    required_action: violation?.required_action || "",
    days_to_cure: violation?.days_to_cure ?? "N/A",
    cure_by: violation?.cure_by ? formatDate(violation.cure_by) : "",
    description: violation?.description || "",
    id: violation?.id || "",
    property_id: violation?.property_id || "",
    status: violation?.status || "",
    resolution: violation?.resolution || "",
    violation_article: violation?.violation_article || "",
  };

  const escapeHtml = (str) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  // 🔹 Replace tags
  const applyTags = (html) => {
    if (!html) return "";
    let output = html;

    Object.entries(tags).forEach(([key, value]) => {
      const escaped = typeof value === "string" ? escapeHtml(value).replace(/\n/g, "<br/>") : value;
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "gi");
      output = output.replace(regex, escaped);
    });

    return output;
  };

  // 🔹 Manual page breaks in template
  const splitContent = (html) =>
    html.split(/<hr[^>]*class=["']?page-break["']?[^>]*>/gi);

  const parts = splitContent(applyTags(content));

  // 🔹 Only image files
  const imageFiles =
    violation.files?.filter((f) => f.file_type?.startsWith("image/")) || [];

  // 🔹 Chunk helper for 2 images per page
  const chunkImages = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const imagePages = chunkImages(imageFiles, 2);

  return (
    <Box
      id="styled-letter"
      sx={{
        backgroundColor: "#fff",
        color: "#222",
        maxWidth: 760,
        mx: "auto",
        fontFamily: `"Nunito Sans", "Helvetica Neue", Arial, sans-serif`,
        fontSize: "11pt",
        lineHeight: 1.6,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <style>{`
        .page {
          break-after: page;
          page-break-after: always;
          padding: 32px 48px;
        }
        .page:last-child {
          break-after: auto;
          page-break-after: auto;
        }
        .pdf-header {
          text-align: center;
          margin-bottom: 12px;
          padding-bottom: 6px;
          border-bottom: 1px solid #ccc;
        }
        .pdf-header img {
          height: 80px;
          margin-bottom: 2px;
        }
      .pdf-header .title {
  font-weight: 700;
  font-size: 14pt;
  margin: 2px 0 0 0;      /* tighter */
  line-height: 1.05;      /* tighter */
}
        .pdf-header .subtitle {
          font-size: 9.5pt;
          color: #555;
          margin: 0;
          line-height: 0.1; 
        }
       #styled-letter p {
  margin: 10px 0;
  font-size: 11pt;
}

#styled-letter .letter-header-block p {
  margin: 2px 0 !important;
  line-height: 1.2;
}
        #styled-letter ul {
          margin: 4px 0 10px 20px;
          padding: 0;
        }
        #styled-letter li {
          margin: 2px 0;
          line-height: 1.3;
        }

       
}
      `}</style>

      {/* MAIN LETTER PAGES */}
      {parts.map((section, i) => (
        <React.Fragment key={i}>
          <div className="page">
            {/* Header */}
            <Box className="pdf-header">
              <img src={logoUrl || logo} alt="HOA Logo" />
              <Typography className="title">
                {type || "Violation Notice"}
              </Typography>
              <Typography className="subtitle">
                {deliveryMethod || "Delivered via US Mail and Email"}
              </Typography>
            </Box>

            {/* Main content */}
            <div
              style={{ fontSize: "inherit", lineHeight: "inherit" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section || '') }}
            />
          </div>

          {/* Only after last TEXT section → add documentation pages */}
          {i === parts.length - 1 && imageFiles.length > 0 && (
            <>
              {imagePages.map((chunk, pageIndex) => (
                <div className="page" key={`img-page-${pageIndex}`}>
                  {/* Header on every documentation page */}
                  <Box className="pdf-header">
                    <img src={logoUrl || logo} alt="HOA Logo" />
                    <Typography className="title">
                      {type || "Violation Notice"}
                    </Typography>
                    <Typography className="subtitle">
                      {deliveryMethod || "Delivered via US Mail and Email"}
                    </Typography>
                  </Box>

                  {/* Documentation title ONLY on first page */}
                  {pageIndex === 0 && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        fontSize: "12pt",
                        borderBottom: "2px solid #ddd",
                        pb: 0.5,
                      }}
                    >
                      Documentation:
                    </Typography>
                  )}

                  {/* Images (2 per page) */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {chunk.map((file) => {
                      const fileUrl = file.file_url?.startsWith("http")
                        ? file.file_url
                        : `${API_BASE_URL}/violationsimages/${violation.id}/${encodeURIComponent(
                            file.file_name
                          )}`;

                      return (
                        <Box
                          key={file.id}
                          sx={{
                            p: 1.5,
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            backgroundColor: "#fafafa",
                            width: "45%",
                            textAlign: "center",
                          }}
                        >
                          <img
                            src={fileUrl}
                            alt={file.file_name || "Evidence image"}
                            crossOrigin="anonymous"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "250px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </div>
              ))}
            </>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ViolationLetterTemplate;
